import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiClient } from "../../../Apis/auth";
import type { GlobalState } from "../../../Interfaces/ReduxSliceInterface";
import type { FacilityItem } from "../../../Interfaces/FacilityInterface";
import type { DoctorItem } from "../../../Interfaces/DoctorInterface";
import type { DepartmentItem } from "../../../Interfaces/DepartmentInterface";
import type { ApiUserLookup } from "../../../Apis/users";
import { fetchAllDepartmentsList } from "../../../Apis/departments";
import { fetchAllDoctorsList } from "../../../Apis/doctors";
import { getUsersLookupApi } from "../../../Apis/users";
import { getOpportunityTypesApi } from "../../../Apis/opportunities";
import type { OpportunityTypeItem } from "../../../Interfaces/OpportunitiesInterface";

const initialState: GlobalState = {
  facilities: [],
  departments: [],
  doctors: [],
  users: [],
  operationDepartments: [],
  facilitiesLoading: false,
  departmentsLoading: false,
  doctorsLoading: false,
  usersLoading: false,
  operationDepartmentsLoading: false,
  opportunityTypes: [],
  opportunityTypesLoading: false,
  error: null,
};

export const fetchFacilities = createAsyncThunk<FacilityItem[]>(
  "global/fetchFacilities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<
        FacilityItem[] | { items?: FacilityItem[] } | FacilityItem
      >("/facilities");
      let data: FacilityItem[] = [];
      const body = response.data;
      if (Array.isArray(body)) {
        data = body;
      } else if (
        body &&
        typeof body === "object" &&
        "items" in body &&
        Array.isArray(body.items)
      ) {
        data = body.items;
      } else if (body && typeof body === "object") {
        data = [body as FacilityItem];
      }
      return data.map((f) => ({
        ...f,
        displayName: f.displayName ?? f.name,
      }));
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch facilities"
      );
    }
  }
);

export const fetchDepartments = createAsyncThunk<DepartmentItem[]>(
  "global/fetchDepartments",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAllDepartmentsList({ limit: 200, isActive: true });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch departments"
      );
    }
  }
);

export const fetchDoctors = createAsyncThunk<DoctorItem[]>(
  "global/fetchDoctors",
  async (_, { rejectWithValue }) => {
    try {
      const items = await fetchAllDoctorsList({ limit: 500, isActive: true });
      return items.map((d) => ({
        id: d._id,
        tenantId: d.tenantId,
        email: d.email,
        phone: d.phone,
        doctorProfile: d.doctorProfile,
        isActive: d.isActive,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      })) as DoctorItem[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch doctors");
    }
  }
);

export const fetchUsers = createAsyncThunk<ApiUserLookup[]>(
  "global/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUsersLookupApi({ page: 1, size: 100 });
      return response?.items ?? [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch users");
    }
  }
);

export const fetchOpportunityTypes = createAsyncThunk<OpportunityTypeItem[]>(
  "global/fetchOpportunityTypes",
  async (_, { rejectWithValue }) => {
    try {
      return await getOpportunityTypesApi({ isActive: "true" });
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown } };
      return rejectWithValue(
        err.response?.data ?? "Failed to fetch opportunity types"
      );
    }
  }
);

export const fetchOperationDepartments = createAsyncThunk<
  GlobalState["operationDepartments"]
>("global/fetchOperationDepartments", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get<{
      data?: unknown;
      items?: unknown;
    }>("/config/operation-departments", {
      params: { page: 1, limit: 200, isActive: true },
    });
    const payload = response.data;
    const items = payload?.data ?? payload?.items ?? payload ?? [];
    const list = Array.isArray(items) ? items : [];
    return list
      .map((d: any) => ({
        id: String(d.id ?? d._id ?? ""),
        name: String(d.name ?? ""),
        code: String(d.code ?? ""),
        facilityIds: Array.isArray(d.facilityIds) ? d.facilityIds : [],
        isActive: d.isActive,
      }))
      .filter((d) => d.id && d.name && d.isActive !== false);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to fetch operation departments"
    );
  }
});

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    clearGlobal: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Facilities
      .addCase(fetchFacilities.pending, (state) => {
        state.facilitiesLoading = true;
      })
      .addCase(fetchFacilities.fulfilled, (state, action) => {
        state.facilitiesLoading = false;
        state.facilities = action.payload;
      })
      .addCase(fetchFacilities.rejected, (state, action) => {
        state.facilitiesLoading = false;
        state.error = action.payload as string;
      })
      // Departments
      .addCase(fetchDepartments.pending, (state) => {
        state.departmentsLoading = true;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.departmentsLoading = false;
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.departmentsLoading = false;
        state.error = action.payload as string;
      })
      // Doctors
      .addCase(fetchDoctors.pending, (state) => {
        state.doctorsLoading = true;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.doctorsLoading = false;
        state.doctors = action.payload;
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.doctorsLoading = false;
        state.error = action.payload as string;
      })
      // Users
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload as string;
      })
      // Opportunity types (tenant catalog)
      .addCase(fetchOpportunityTypes.pending, (state) => {
        state.opportunityTypesLoading = true;
      })
      .addCase(fetchOpportunityTypes.fulfilled, (state, action) => {
        state.opportunityTypesLoading = false;
        state.opportunityTypes = action.payload;
      })
      .addCase(fetchOpportunityTypes.rejected, (state, action) => {
        state.opportunityTypesLoading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch opportunity types";
      })
      // Operation departments
      .addCase(fetchOperationDepartments.pending, (state) => {
        state.operationDepartmentsLoading = true;
      })
      .addCase(fetchOperationDepartments.fulfilled, (state, action) => {
        state.operationDepartmentsLoading = false;
        state.operationDepartments = action.payload;
      })
      .addCase(fetchOperationDepartments.rejected, (state, action) => {
        state.operationDepartmentsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearGlobal } = globalSlice.actions;

export default globalSlice.reducer;
